import { zodResolver } from '@hookform/resolvers/zod';
import { CompanyPatch, UNITS, type Company } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm, type Path, type PathValue } from 'react-hook-form';
import type { z } from 'zod';

import { companyApi, errorMessage, exportsApi, qk } from '@/api';
import { ErrorState, LoadingState, PageHeader } from '@/components/PageHeader';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { Card, CardBody, CardHeader } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { Switch } from '@/components/ui/toggle';
import { useCompanyBasics, useImpersonation, useSession } from '@/features/auth/session';
import { cn } from '@/lib/cn';
import { fmtWhen } from '@/lib/dates';

type FormInput = z.input<typeof CompanyPatch>;
type FormOutput = z.output<typeof CompanyPatch>;

const DAYS: Array<[Company['weekStartDay'], string]> = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

/** W11 — every doc-17 key editable; report layout with preview; automatic emails; export all data; danger zone. */
export function SettingsPage() {
  const company = useQuery({ queryKey: qk.company(), queryFn: companyApi.getCompany });
  if (company.isPending) return <LoadingState />;
  if (company.isError)
    return <ErrorState error={company.error} onRetry={() => void company.refetch()} />;
  return <SettingsForm key={company.data.syncVersion} company={company.data} />;
}

function SettingsForm({ company }: { company: Company }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const { tz } = useCompanyBasics();
  const { impersonating } = useImpersonation();
  const { id: _id, logoUrl: _logo, syncVersion: _v, ...defaults } = company;
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(CompanyPatch),
    defaultValues: defaults,
  });
  const [emailsText, setEmailsText] = useState(company.clientDefaultEmails.join(', '));
  const [summaryTo, setSummaryTo] = useState(company.ownerSummaryEmail.recipients.join(', '));
  const logoInput = useRef<HTMLInputElement>(null);
  const [logoName, setLogoName] = useState<string | null>(company.logoUrl ? 'Current logo' : null);
  const [logoPreview, setLogoPreview] = useState<string | null>(company.logoUrl);

  const save = useMutation({
    mutationFn: (patch: FormOutput) => companyApi.patchCompany(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.company() });
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.dashboard.all() });
      toast.success('Settings saved');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  // The logo saves on its own (no Save needed); only the session is refreshed so unsaved edits on the form survive.
  const uploadLogo = useMutation({
    mutationFn: companyApi.uploadLogo,
    onSuccess: (r) => {
      setLogoPreview(r.logoUrl);
      void qc.invalidateQueries({ queryKey: qk.me() });
      toast.success('Logo updated');
    },
    onError: (e) => {
      setLogoPreview(company.logoUrl);
      setLogoName(company.logoUrl ? 'Current logo' : null);
      toast.error(errorMessage(e));
    },
  });

  const splitEmails = (s: string) =>
    s
      .split(/[,;\s]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  const onSubmit = form.handleSubmit(
    (v) => {
      const patch: FormOutput = {
        ...v,
        clientDefaultEmails: splitEmails(emailsText),
        ownerSummaryEmail: {
          ...(v.ownerSummaryEmail ?? company.ownerSummaryEmail),
          recipients: splitEmails(summaryTo),
        },
      };
      const check = CompanyPatch.safeParse(patch);
      if (!check.success) {
        toast.error(check.error.issues[0]?.message ?? 'Check the highlighted fields');
        return;
      }
      save.mutate(check.data);
    },
    () => toast.error('Check the highlighted fields'),
  );

  const errors = form.formState.errors;
  const w = form.watch;
  const units = w('unitsEnabled') ?? [];
  const bands = w('productionBands') ?? company.productionBands;
  const sections = w('reportConfig.sections') ?? company.reportConfig.sections;
  const set = <K extends Path<FormInput>>(k: K, v: PathValue<FormInput, K>) =>
    form.setValue(k, v, { shouldDirty: true });

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        title="Settings"
        actions={
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={impersonating}
            title={impersonating ? 'Support sessions are read-only' : undefined}
            data-testid="save-settings"
          >
            Save changes
          </Button>
        }
      />
      <div className="two">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Company" />
            <CardBody className="form-grid">
              <Field label="Company name" htmlFor="s-name" error={errors.name?.message}>
                <Input id="s-name" invalid={!!errors.name} {...form.register('name')} />
              </Field>
              <Field
                label="Logo"
                hint="PNG or JPEG up to 2 MB; shown on reports and in the header."
              >
                <div className="flex h-[34px] items-center gap-2 rounded-lg border border-line bg-surface px-2.5">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Company logo"
                      data-testid="logo-preview"
                      className="size-5 rounded object-contain"
                    />
                  ) : (
                    <span className="inline-block size-5 rounded bg-orange" aria-hidden />
                  )}
                  <span className="truncate">
                    {uploadLogo.isPending ? 'Uploading…' : (logoName ?? 'No logo yet')}
                  </span>
                  <button
                    type="button"
                    className="ml-auto font-semibold text-orange-ink"
                    onClick={() => logoInput.current?.click()}
                  >
                    {logoName ? 'Replace' : 'Upload'}
                  </button>
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (f.size > 2 * 1024 * 1024) {
                        toast.error('Logo must be 2 MB or smaller');
                        return;
                      }
                      setLogoName(f.name);
                      setLogoPreview(URL.createObjectURL(f));
                      uploadLogo.mutate(f);
                    }}
                  />
                </div>
              </Field>
              <Field label="Timezone" htmlFor="s-tz" error={errors.timezone?.message}>
                <Select id="s-tz" {...form.register('timezone')}>
                  {[
                    'America/New_York',
                    'America/Chicago',
                    'America/Denver',
                    'America/Phoenix',
                    'America/Los_Angeles',
                    'America/Anchorage',
                    'Pacific/Honolulu',
                  ].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Standard day hours"
                htmlFor="s-hours"
                error={errors.standardDayHours?.message}
                hint="Default hours on every crew and machine chip."
              >
                <Input
                  id="s-hours"
                  inputMode="decimal"
                  invalid={!!errors.standardDayHours}
                  {...form.register('standardDayHours')}
                />
              </Field>
              <Field
                label="Cutoff time"
                htmlFor="s-cutoff"
                error={errors.cutoffTime?.message}
                hint="Dashboard flags days not submitted by then."
              >
                <Input
                  id="s-cutoff"
                  type="time"
                  invalid={!!errors.cutoffTime}
                  {...form.register('cutoffTime')}
                />
              </Field>
              <Field label="Week starts on" htmlFor="s-week">
                <Select id="s-week" {...form.register('weekStartDay')}>
                  {DAYS.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Units enabled"
                wide
                hint="Offered in the cost code unit picker."
                error={errors.unitsEnabled?.message}
              >
                <div className="flex flex-wrap gap-1.5">
                  {UNITS.map((u) => {
                    const on = units.includes(u);
                    return (
                      <button
                        key={u}
                        type="button"
                        aria-pressed={on}
                        data-testid={`unit-${u}`}
                        onClick={() =>
                          set('unitsEnabled', on ? units.filter((x) => x !== u) : [...units, u])
                        }
                      >
                        <Pill
                          tone={on ? 'info' : 'neutral'}
                          className={cn('cursor-pointer', !on && 'opacity-70')}
                        >
                          {u}
                        </Pill>
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field
                label="Production bands"
                hint="green ≥ · amber ≥ percent of plan/day"
                error={
                  errors.productionBands?.green?.message ?? errors.productionBands?.amber?.message
                }
              >
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Green threshold"
                    type="number"
                    min={0}
                    max={200}
                    value={bands.green}
                    onChange={(e) =>
                      set('productionBands', { ...bands, green: Number(e.target.value) })
                    }
                    className="w-20"
                  />
                  <span className="text-ink3">green ≥ %</span>
                  <Input
                    aria-label="Amber threshold"
                    type="number"
                    min={0}
                    max={200}
                    value={bands.amber}
                    onChange={(e) =>
                      set('productionBands', { ...bands, amber: Number(e.target.value) })
                    }
                    className="w-20"
                  />
                  <span className="text-ink3">amber ≥ %</span>
                </div>
              </Field>
              <Field
                label="Reopen window"
                htmlFor="s-reopen"
                error={errors.reopenWindowHours?.message}
                hint="Hours a foreman can reopen a finished day."
              >
                <div className="flex items-center gap-2">
                  <Input
                    id="s-reopen"
                    type="number"
                    min={0}
                    max={168}
                    className="w-24"
                    {...form.register('reopenWindowHours', { valueAsNumber: true })}
                  />
                  <span className="text-ink3">hours</span>
                </div>
              </Field>
              <Field
                label="Client report emails (default)"
                htmlFor="s-emails"
                wide
                hint="Jobs can override this list. Comma separated."
              >
                <Input
                  id="s-emails"
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                />
              </Field>
              <ToggleField
                label="Foremen can create a job"
                value={w('foremanCanCreateJob') ?? company.foremanCanCreateJob}
                onChange={(v) => set('foremanCanCreateJob', v)}
              />
              <ToggleField
                label="Foremen can add a worker"
                value={w('foremanCanAddWorker') ?? company.foremanCanAddWorker}
                onChange={(v) => set('foremanCanAddWorker', v)}
              />
              <ToggleField
                label="Foremen can add a cost code"
                value={w('foremanCanAddCostCode') ?? company.foremanCanAddCostCode}
                onChange={(v) => set('foremanCanAddCostCode', v)}
              />
              <ToggleField
                label="Upload photos on cellular"
                value={w('photoUploadOnCellular') ?? company.photoUploadOnCellular}
                onChange={(v) => set('photoUploadOnCellular', v)}
              />
              <div className="w flex flex-col gap-1" data-testid="support-access">
                <Switch
                  checked={w('supportAccessAllowed') ?? company.supportAccessAllowed}
                  onChange={(v) => set('supportAccessAllowed', v)}
                  label="Allow Fuorix support to view this company (read-only)"
                  aria-label="Allow Fuorix support to view this company (read-only)"
                />
                <span className="text-[11px] text-ink3">
                  Lets Fuorix support open your dashboard exactly as you see it to help with a
                  question. They can never change or approve anything, and every visit is logged.
                </span>
              </div>
              <Field label="QuickBooks export mode" htmlFor="s-qbo">
                <Select id="s-qbo" {...form.register('qboExportMode')}>
                  <option value="time_activity">Time activities (labor)</option>
                  <option value="csv">CSV in the QuickBooks import layout</option>
                </Select>
              </Field>
            </CardBody>
          </Card>
          <ExportCard tz={tz} />
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader
              title="Report layout"
              right={<PreviewButton dirty={form.formState.isDirty} />}
            />
            <CardBody className="form-grid">
              <Field label="Photo size" htmlFor="s-photo">
                <Select id="s-photo" {...form.register('reportConfig.photoSize')}>
                  <option value="large">Large · 2 per row</option>
                  <option value="small">Small · 3 per row</option>
                </Select>
              </Field>
              <Field
                label="Footer text"
                htmlFor="s-footer"
                error={errors.reportConfig?.footerText?.message}
              >
                <Input id="s-footer" {...form.register('reportConfig.footerText')} />
              </Field>
              {(
                [
                  ['weather', 'Weather'],
                  ['delays', 'Delays & extra work'],
                  ['notes', 'Notes'],
                  ['photos', 'Photos'],
                  ['toDate', 'To-date vs planned'],
                ] as const
              ).map(([k, label]) => (
                <ToggleField
                  key={k}
                  label={label}
                  value={sections[k] ?? true}
                  onChange={(v) =>
                    set('reportConfig', {
                      ...(w('reportConfig') ?? company.reportConfig),
                      sections: { ...sections, [k]: v },
                    })
                  }
                />
              ))}
              <ToggleField
                label="Cost estimates"
                value={
                  w('reportConfig.showCostEstimates') ?? company.reportConfig.showCostEstimates
                }
                onChange={(v) =>
                  set('reportConfig', {
                    ...(w('reportConfig') ?? company.reportConfig),
                    showCostEstimates: v,
                  })
                }
              />
              <ToggleField
                wide
                label='Watermark "Field copy" on phone-shared PDFs'
                value={
                  w('reportConfig.watermarkFieldCopy') ?? company.reportConfig.watermarkFieldCopy
                }
                onChange={(v) =>
                  set('reportConfig', {
                    ...(w('reportConfig') ?? company.reportConfig),
                    watermarkFieldCopy: v,
                  })
                }
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Automatic emails" />
            <CardBody className="form-grid">
              <ToggleField
                label="Auto-send daily reports to clients"
                value={w('autoSendReports') ?? company.autoSendReports}
                onChange={(v) => set('autoSendReports', v)}
              />
              <Field label="At" htmlFor="s-autosend" error={errors.autoSendTime?.message}>
                <Input id="s-autosend" type="time" {...form.register('autoSendTime')} />
              </Field>
              <ToggleField
                label="Owner summary email"
                value={w('ownerSummaryEmail.enabled') ?? company.ownerSummaryEmail.enabled}
                onChange={(v) =>
                  set('ownerSummaryEmail', {
                    ...(w('ownerSummaryEmail') ?? company.ownerSummaryEmail),
                    enabled: v,
                  })
                }
              />
              <Field
                label="At · to"
                htmlFor="s-summary-time"
                error={errors.ownerSummaryEmail?.time?.message}
              >
                <div className="flex gap-2">
                  <Input
                    id="s-summary-time"
                    type="time"
                    className="w-32"
                    {...form.register('ownerSummaryEmail.time')}
                  />
                  <Input
                    aria-label="Summary recipients"
                    value={summaryTo}
                    onChange={(e) => setSummaryTo(e.target.value)}
                    placeholder="owner@company.com"
                  />
                </div>
              </Field>
            </CardBody>
          </Card>
          <div className="rounded-xl border border-bad bg-hi-bad px-4 py-3.5">
            <b className="text-bad">Danger zone</b>
            <p className="my-1 mb-2 text-xs text-ink2">
              Deleting the company removes every entry, photo and report after a final export. Owner
              only, handled by Fuorix support.
            </p>
            <a
              href={`mailto:support@fuorix.example?subject=${encodeURIComponent(`SiteDay deletion request — ${company.name}`)}&body=${encodeURIComponent(`Please delete the SiteDay company "${company.name}" (${company.id}) after a final export.\n\nRequested by ${user.name} (${user.email ?? ''}).`)}`}
            >
              <Button
                variant="danger"
                size="sm"
                disabled={user.role !== 'owner'}
                title={user.role !== 'owner' ? 'Owner only' : undefined}
              >
                Request deletion…
              </Button>
            </a>
          </div>
        </div>
      </div>
    </form>
  );
}

function ToggleField({
  label,
  value,
  onChange,
  wide,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  wide?: boolean | undefined;
}) {
  return (
    <div className={cn('flex items-center', wide && 'w')}>
      <Switch checked={value} onChange={onChange} label={label} />
    </div>
  );
}

/**
 * Opens GET /company/report-preview.pdf: the shared sample day rendered under the
 * saved report layout (save first to preview unsaved toggles).
 */
function PreviewButton({ dirty }: { dirty: boolean }) {
  return (
    <a
      href={companyApi.reportPreviewUrl()}
      target="_blank"
      rel="noreferrer"
      data-testid="report-preview-link"
      title={dirty ? 'Save changes first to preview them' : 'Opens a sample day as a PDF'}
    >
      <Button size="sm">Preview sample PDF</Button>
    </a>
  );
}

function ExportCard({ tz }: { tz: string }) {
  const toast = useToast();
  const [exportId, setExportId] = useState<string | null>(null);
  const request = useMutation({
    mutationFn: exportsApi.requestExport,
    onSuccess: (r) => {
      setExportId(r.exportId);
      toast.info('Export queued — we will email a link when it is ready');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const status = useQuery({
    queryKey: qk.exports.detail(exportId ?? ''),
    queryFn: () => exportsApi.getExport(exportId!),
    enabled: !!exportId,
    refetchInterval: (q) =>
      q.state.data?.status === 'ready' || q.state.data?.status === 'failed' ? false : 2000,
  });
  useEffect(() => {
    if (status.data?.status === 'ready') toast.success('Your export is ready to download');
    if (status.data?.status === 'failed') toast.error('Export failed — try again');
  }, [status.data?.status, toast]);
  const s = status.data;
  const busy = request.isPending || (!!exportId && s?.status !== 'ready' && s?.status !== 'failed');
  const counts = s?.counts;
  return (
    <Card>
      <CardHeader title="Export all data" />
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => request.mutate()} loading={busy} data-testid="request-export">
            Request export (ZIP)
          </Button>
          <span className="text-xs text-ink3">
            CSV per table, every PDF and photo. Link emailed to owners and admins when ready, valid
            7 days.
          </span>
        </div>
        {busy && exportId ? (
          <p className="text-xs text-ink2" role="status">
            Building your export{s?.status === 'running' ? ' (rendering PDFs)' : ''}… this page
            keeps checking; the link is also emailed.
          </p>
        ) : null}
        {s?.status === 'ready' ? (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
            data-testid="export-ready"
          >
            {s.downloadUrl ? (
              <a
                className="font-semibold text-orange-ink underline"
                href={s.downloadUrl}
                data-testid="export-download"
              >
                Download ZIP
              </a>
            ) : (
              <span className="font-semibold text-bad">
                This export link has expired — request a new one.
              </span>
            )}
            {s.expiresAt ? (
              <span className="text-ink3">Link valid until {fmtWhen(s.expiresAt, tz)}</span>
            ) : null}
            {counts ? (
              <span className="text-ink3">
                {counts['day_entries'] ?? 0} day entries · {counts['pdfs'] ?? 0} PDFs ·{' '}
                {counts['photoFiles'] ?? 0} photos · {counts['users'] ?? 0} people ·{' '}
                {counts['jobs'] ?? 0} jobs
              </span>
            ) : null}
          </div>
        ) : null}
        {s?.status === 'failed' ? (
          <span className="text-xs font-semibold text-bad">
            Export failed{s.error ? ` — ${s.error}` : ''}. Try again.
          </span>
        ) : null}
      </CardBody>
    </Card>
  );
}
